import {
  Box,
  CircularProgress,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography
} from "@mui/material";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import { callAPI, Method } from "../../commonFunctions/CommonFunctions";
import WSButton from "../../Components/Button/WSButton";
import { useSnackbar } from "../../Components/Snackbar/SnackBarContext";
import { NotFound } from "../NotFound";

const OrderPrint = () => {
  const { id } = useParams();
  const { showMessage } = useSnackbar();

  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    async function getDataById() {
      try {
        const response: any = await callAPI("workorders", Method.GET, null, id);
        if (response?.data) {
          setData(response.data);
        }
      } catch (error) {
        showMessage("Something went wrong. Please try again.", "error");
      } finally {
        setLoading(false);
      }
    }

    if (id) {
      getDataById();
    }
  }, [id]);

  // 🖨️ Print Handler
  const handlePrint = useReactToPrint({
    contentRef
  });

  if (loading)
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  if (!data) return <NotFound />;

  return (
    <div>
      <WSButton
        id="printBtn"
        label="Print Order"
        variant="contained"
        color="primary"
        onClick={handlePrint}
        size="small"
        sx={{ marginBottom: 2 }}
      />
      <Paper
        ref={contentRef}
        style={{ padding: 20, maxWidth: 600, margin: "auto" }}
      >
        {/* Header Section */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 2
          }}
        >
          <Box sx={{ textAlign: "center" }}>
            {/* Company Logo */}
            <img
              src="/path-to-logo.png"
              alt="Company Logo"
              style={{ width: 120, marginBottom: 4 }}
            />
            <Typography sx={{ fontWeight: "bold", fontSize: 14 }}>
              NADUKANI, PALLIVAYAL P.O. - 670142
            </Typography>
          </Box>

          {/* Contact Details */}
          <Box sx={{ textAlign: "right" }}>
            <Typography sx={{ fontWeight: "bold", fontSize: 14 }}>
              📞 9747 535 535
            </Typography>
            <Typography sx={{ fontWeight: "bold", fontSize: 14 }}>
              📞 9544 541 114
            </Typography>
            <Typography sx={{ fontWeight: "bold", fontSize: 14 }}>
              📞 9544 541 117
            </Typography>
          </Box>
        </Box>

        <Box sx={{ borderBottom: "1px solid black", marginBottom: 2 }}></Box>

        {/* Order Form Title */}
        <Box sx={{ textAlign: "center", marginBottom: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: "bold" }}>
            ORDER FORM
          </Typography>
          <Typography>
            Date: {dayjs(data?.date).format("DD/MM/YYYY")}
          </Typography>
        </Box>

        {/* Client & Order Details */}
        <TableContainer>
          <Table>
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold" }}>
                  Name of Client
                </TableCell>
                <TableCell>{data?.customerName || "N/A"}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold" }}>Address</TableCell>
                <TableCell>{data?.address || "N/A"}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold" }}>Phone</TableCell>
                <TableCell>{data?.phone || "N/A"}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold" }}>
                  Approximate Area
                </TableCell>
                <TableCell>{data?.approximateArea || "N/A"}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold" }}>Rate</TableCell>
                <TableCell>{data?.rate || 0}/-</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold" }}>
                  Date of Commencement
                </TableCell>
                <TableCell>
                  {dayjs(data?.commencement).format("DD/MM/YYYY")}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold" }}>
                  Date of Completion
                </TableCell>
                <TableCell>
                  {dayjs(data?.completion).format("DD/MM/YYYY")}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {/* Specification & Required Items */}
        <Typography variant="h6" sx={{ marginTop: 3, fontWeight: "bold" }}>
          Specification & Required Items
        </Typography>
        <TableContainer>
          <Table>
            <TableBody>
              {data?.quotationItems?.map((item: any, index: number) => (
                <TableRow key={index}>
                  <TableCell>{item?.productName || "N/A"}</TableCell>
                  <TableCell>
                    {item?.quantity} {item?.unit}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Terms and Condtion */}

        <Box mt={2}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
            Terms and Conditions.
          </Typography>
          <Typography variant="body1" sx={{ pl: 2 }}>
            ➤ {data.termsAndConditions}
          </Typography>
        </Box>

        {/* Payment Terms */}
        <Box mt={4}>
          <Typography variant="h6" color="primary">
            Mode of Payment:
          </Typography>
          <ul>
            <li>
              <Typography>
                <b>30%</b> of total value of work on order confirmation.
              </Typography>
            </li>
            <li>
              <Typography>
                <b>60%</b> of total value of work on delivery of material to
                site.
              </Typography>
            </li>
            <li>
              <Typography>
                <b>10%</b> balance after completion of work.
              </Typography>
            </li>
          </ul>
        </Box>
        

        {/* Remarks */}
        <Typography variant="h6" sx={{ marginTop: 3, fontWeight: "bold" }}>
          Remarks
        </Typography>
        <Typography variant="body2">{data?.remarks || "N/A"}</Typography>

        {/* Footer */}
        <Divider sx={{ my: 3 }} />
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-end"
        >
          <Box>
            <Typography fontWeight="bold">Accepted:</Typography>
            <Typography>Name of Client: ___________________</Typography>
            <Typography>Witnesses:</Typography>
            <Typography>1. ___________________</Typography>
            <Typography>2. ___________________</Typography>
          </Box>
          <Box textAlign="right">
            <Typography>Thanks & Regards,</Typography>
            <Typography fontWeight="bold">JOSEPH PU</Typography>
            <Typography>Managing Director</Typography>
          </Box>
        </Box>
      </Paper>
    </div>
  );
};

export default OrderPrint;
